import { BrowserAgent } from "@newrelic/browser-agent/loaders/browser-agent";
import { APP_VERSION } from "@/config/app-version";
import config from "@/config";
import {
  isLocalNewRelicEnvironment,
  resolveNewRelicEnvironment,
} from "./environment";

export type BrowserAgentApi = Pick<
  BrowserAgent,
  | "log"
  | "noticeError"
  | "setUserId"
  | "setCustomAttribute"
  | "setApplicationVersion"
> &
  Partial<Pick<BrowserAgent, "setCurrentRouteName" | "setPageViewName">>;

export type BrowserAgentLoggingApi = Pick<
  BrowserAgentApi,
  "log" | "noticeError"
>;

type ShouldInitNewRelicOptions = {
  hasNewRelicConfig: boolean;
  dev: boolean;
  newrelicLocal: boolean;
  hostname: string;
};

let browserAgent: BrowserAgentApi | undefined;

function isLocalHost(hostname: string) {
  return hostname === "localhost" || hostname === "127.0.0.1";
}

export function shouldInitNewRelic(options: ShouldInitNewRelicOptions) {
  if (!options.hasNewRelicConfig) return false;
  if (options.newrelicLocal) return true;
  if (options.dev) return false;
  if (isLocalHost(options.hostname)) return false;
  return true;
}

export function getBrowserAgent(): BrowserAgentApi | undefined {
  return browserAgent;
}

export function initNewRelic() {
  const newrelic = config.newrelic;
  const newrelicLocal = import.meta.env.VITE_NEWRELIC_LOCAL === "true";
  if (
    !newrelic ||
    !shouldInitNewRelic({
      hasNewRelicConfig: true,
      dev: import.meta.env.DEV,
      newrelicLocal,
      hostname: window.location.hostname,
    })
  ) {
    return;
  }

  const { applicationID, licenseKey, accountID, trustKey } = newrelic;

  const agent = new BrowserAgent({
    init: {
      browser_consent_mode: { enabled: false },
      privacy: { cookies_enabled: true },
      distributed_tracing: { enabled: true },
      performance: { capture_measures: true },
      ajax: { deny_list: ["bam.nr-data.net"], capture_payloads: "none" },
    },
    loader_config: {
      accountID,
      trustKey,
      agentID: applicationID,
      licenseKey,
      applicationID,
    },
    info: {
      beacon: "bam.nr-data.net",
      errorBeacon: "bam.nr-data.net",
      licenseKey,
      applicationID,
      sa: 1,
    },
  });

  agent.setApplicationVersion(APP_VERSION);
  agent.setCustomAttribute(
    "environment",
    resolveNewRelicEnvironment({
      configured: config.environment,
      local: isLocalNewRelicEnvironment({
        dev: import.meta.env.DEV,
        appMode: import.meta.env.VITE_APP_MODE,
        newrelicLocal,
      }),
    })
  );
  agent.setCustomAttribute("host", window.location.hostname);

  browserAgent = agent;
}
